import {createAsyncThunk, createSlice} from "@reduxjs/toolkit";
import {
    convertQuoteToJob,
    createQuote,
    fetchQuoteById,
    fetchQuotes,
    fetchQuoteStatuses,
    fetchQuoteTemplates, releaseJob, resubmitJob, removeQuoteTemplates,
    updateQuote,
    updateQuoteStatus, updateQuoteTemplate
} from "../API/api";
import Moment from "moment";

export const getQuotes = createAsyncThunk(
    'projects/getQuotes',
    async (_,{rejectWithValue})=> {
        try {
            const response = await fetchQuotes();
            return response.data;
        } catch (err) {
            return rejectWithValue(err.response.data);
        }
    }
);

export const getQuoteTemplates = createAsyncThunk(
    'projects/getQuoteTemplates',
    async (_,{rejectWithValue})=> {
        try {
            const response = await fetchQuoteTemplates();
            return response.data;
        } catch (err) {
            return rejectWithValue(err.response.data);
        }
    }
);

export const putQuoteTemplate = createAsyncThunk(
    "projects/updateQuoteTemplate",
    async (template, { rejectWithValue }) => {
        try {
            const response = await updateQuoteTemplate(template.id, template.payload);
            return response.data;

        } catch (err) {
            return rejectWithValue(err.response.data);
        }
    }
);

export const deleteQuoteTemplates = createAsyncThunk(
    "projects/deleteQuoteTemplates",
    async (request, { rejectWithValue }) => {
        try {
            const response = await removeQuoteTemplates(request.payload);
            return response.data;
        } catch (err) {
            return rejectWithValue(err.response?.data || "An error occurred");
        }
    }
);

export const getQuoteStatuses = createAsyncThunk(
    'projects/getQuoteStatuses',
    async (_, {rejectWithValue}) => {
        try {
            const response = await fetchQuoteStatuses();
            return response.data;
        } catch (err) {
            return rejectWithValue(err.response.data);
        }
    }
);

export const getQuoteById = createAsyncThunk(
    'projects/getQuoteById',
    async (quoteId, { rejectWithValue }) => {
        try {
            const response = await fetchQuoteById(quoteId);
            return response.data; 
        } catch (err) {
            return rejectWithValue(err.response.data);
        }
    }
);

export const postNewQuote = createAsyncThunk(
    "projects/newQuote",
    async (newQuote, {rejectWithValue}) => {
        try {
            const response = await createQuote(newQuote);
            return response.data;
        } catch (err) {
            return rejectWithValue(err.response.data);
        }
    }
);

export const postConvertToJob = createAsyncThunk(
    "projects/convertToJob",
    async (request, {rejectWithValue}) => {
        try {
            const response = await convertQuoteToJob(request.id, request.payload);
            return response.data;
        } catch (err) {
            return rejectWithValue(err.response.data);
        }
    }
);

export const postReleaseJob = createAsyncThunk(
    "projects/releaseJob",
    async (request, {rejectWithValue}) => {
        try {
            const response = await releaseJob(request.id, request.payload);
            return response.data;
        } catch (err) {
            return rejectWithValue(err.response.data);
        }
    }
)

export const postResubmitJob = createAsyncThunk(
    "projects/resubmitJob",
    async (request, {rejectWithValue}) => {
        try {
            const response = await resubmitJob(request.id, request.payload);
            return response.data;
        } catch (err) {
            return rejectWithValue(err.response.data);
        }
    }
)

export const putQuote = createAsyncThunk(
    "projects/updateQuote",
    async (quote, { rejectWithValue }) => {
        try {
            const response = await updateQuote(quote.id, quote.payload);
            return response.data;

        } catch (err) {
            return rejectWithValue(err.response.data);
        }
    }
);

export const patchQuoteStatus = createAsyncThunk(
    "projects/updateQuoteStatus",
    async (quote, {rejectWithValue}) => {
        try {
            const response = await updateQuoteStatus(quote.id, quote.payload);
            return {
                id: quote.id,
                requestBody: quote.payload,
                response: response
            };
        } catch (err) {
            return rejectWithValue(err.response.data);
        }
    }
);

const projectsSlice = createSlice({
    name: "projects",
    initialState: {
        getQuotes: {
            status: 'idle',
            array: [],
            error: null
        },
        getQuoteTemplates: {
            status: 'idle',
            byId: [],
            error: null
        },
        putQuoteTemplate: {
            status: 'idle',
            error: null
        },
        deleteQuoteTemplates: {
            status: 'idle',
            error: null
        },
        getQuoteById: {
            status: 'idle',
            quotesById: {},
            error: null
        },
        getQuoteStatuses: {
            status: 'idle',
            quoteStatuses: [],
            error: null
        },
        postNewQuote: {
            status: 'idle',
            error: null
        },
        postConvertToJob: {
            status: 'idle',
            error: null
        },
        postReleaseJob: {
            status: 'idle',
            error: null
        },
        postResubmitJob: {
            status: 'idle',
            error: null
        },
        putQuote: {
            status: 'idle',
            error: null
        },
        patchQuoteStatus: {
            status: 'idle',
            error: null
        }
    },
    reducers: {
        quoteUpdatedFromStream: (state, action) => {
            const updatedQuote = action.payload;
            if (state.getQuoteById.quotesById[updatedQuote.id]) {
                state.getQuoteById.quotesById[updatedQuote.id] = {
                    ...state.getQuoteById.quotesById[updatedQuote.id],
                    ...updatedQuote
                };
            }

            const quoteIndex = state.getQuotes.array.findIndex(quote => quote.id === updatedQuote.id);
            if (quoteIndex !== -1) {
                state.getQuotes.array[quoteIndex] = {
                    ...state.getQuotes.array[quoteIndex],
                    ...updatedQuote
                };
            }
        }
    },
    extraReducers: (builder) => {
        builder
            // Get Projects
            .addCase(getQuotes.pending, (state) => {
                state.getQuotes.status = 'loading';
            })
            .addCase(getQuotes.fulfilled, (state, action) => {
                state.getQuotes.status = 'succeeded';
                state.getQuotes.array = action.payload.quotes.map(quote => ({
                    ...quote,
                    createdAt: Moment(quote.createdAt).format('D MMM YYYY, h:mma')
                }));
            }) 
            .addCase(getQuotes.rejected, (state, action) => {
                state.getQuotes.status = 'failed';
                state.getQuotes.error = action.error.message;
            })
            // Get Quote Templates
            .addCase(getQuoteTemplates.pending, (state) => {
                state.getQuoteTemplates.status = 'loading';
            })
            .addCase(getQuoteTemplates.fulfilled, (state, action) => {
                state.getQuoteTemplates.status = 'succeeded';
                state.getQuoteTemplates.byId = action.payload;
            })
            .addCase(getQuoteTemplates.rejected, (state, action) => {
                state.getQuoteTemplates.status = 'failed';
                state.getQuoteTemplates.error = action.error.message;
            })
            // Update Quote Template
            .addCase(putQuoteTemplate.pending, (state) => {
                state.putQuoteTemplate.status = 'loading';
            })
            .addCase(putQuoteTemplate.fulfilled, (state, action) => {
                state.putQuoteTemplate.status = 'succeeded';
                const existingTemplateIndex = state.getQuoteTemplates.byId.findIndex((x) => x.id === action.payload.id);
                if (existingTemplateIndex !== -1) {
                    state.getQuoteTemplates.byId[existingTemplateIndex] = action.payload;
                }
            })
            .addCase(putQuoteTemplate.rejected, (state, action) => {
                state.putQuoteTemplate.status = 'failed';
                state.putQuoteTemplate.error = action.error.message;
            })
            // Delete Quote Templates
            .addCase(deleteQuoteTemplates.pending, (state) => {
                state.deleteQuoteTemplates.status = 'loading';
            })
            .addCase(deleteQuoteTemplates.fulfilled, (state, action) => {
                state.deleteQuoteTemplates.status = 'succeeded';
                let existingTemplates = [];

                action.meta.arg.payload.ids.forEach((id) => {
                    existingTemplates.push(
                        state.getQuoteTemplates.byId.findIndex((x) => x.id === id)
                    );
                });

                if (existingTemplates.length > 0) {
                    state.getQuoteTemplates.byId =
                        state.getQuoteTemplates.byId.filter(
                            (_, index) => !existingTemplates.includes(index)
                        );
                }
            })
            .addCase(deleteQuoteTemplates.rejected, (state, action) => {
                state.deleteQuoteTemplates.status = 'failed';
                state.deleteQuoteTemplates.error = action.error.message;
            })
            // Get Quote Statuses
            .addCase(getQuoteStatuses.pending, (state) => {
                state.getQuoteStatuses.status = 'loading';
            })
            .addCase(getQuoteStatuses.fulfilled, (state, action) => {
                state.getQuoteStatuses.status = 'succeeded';
                state.getQuoteStatuses.quoteStatuses = action.payload.quoteStatuses;
            })
            .addCase(getQuoteStatuses.rejected, (state, action) => {
                state.getQuoteStatuses.status = 'failed';
                state.getQuoteStatuses.error = action.error.message;
            })
            // Get Quote by Id
            .addCase(getQuoteById.pending, (state) => {
                state.getQuoteById.status = 'loading';
            })
            .addCase(getQuoteById.fulfilled, (state, action) => {
                state.getQuoteById.status = 'succeeded';
                state.getQuoteById.quotesById[action.payload.id] = action.payload;
            })
            .addCase(getQuoteById.rejected, (state, action) => {
                state.getQuoteById.status = 'failed';
                state.getQuoteById.error = action.error.message;
            })
            // Create New Quote
            .addCase(postNewQuote.pending, (state) => {
                state.postNewQuote.status = 'loading';
            })
            .addCase(postNewQuote.fulfilled, (state, action) => {
                state.postNewQuote.status = 'succeeded';
                state.getQuotes.array.unshift({
                    ...action.payload,
                    createdAt: Moment(action.payload.createdAt).format('D MMM YYYY, h:mma')
                });
                // Trigger a re-grab of templates
                state.getQuoteTemplates.status = 'idle';
            })
            .addCase(postNewQuote.rejected, (state, action) => {
                state.postNewQuote.status = 'failed';
                state.postNewQuote.error = action.error.message;
            })
            // Update Quote
            .addCase(putQuote.pending, (state) => {
                state.putQuote.status = 'loading';
            })
            .addCase(putQuote.fulfilled, (state, action) => {
                state.putQuote.status = 'succeeded';
                state.getQuoteById.quotesById[action.payload.id] = action.payload;
            })
            .addCase(putQuote.rejected, (state, action) => {
                state.putQuote.status = 'failed';
                state.putQuote.error = action.error.message;
            })
            // Update Quote Status
            .addCase(patchQuoteStatus.pending, (state) => {
                state.patchQuoteStatus.status = 'loading';
            })
            .addCase(patchQuoteStatus.fulfilled, (state, action) => {
                state.patchQuoteStatus.status = 'succeeded';
                const quoteToUpdate = state.getQuotes.array.find(quote => quote.id === action.payload.id);
                if (quoteToUpdate) {
                    quoteToUpdate.quoteStatus = action.payload.requestBody.quoteStatusAsText;
                }
            })
            .addCase(patchQuoteStatus.rejected, (state, action) => {
                state.patchQuoteStatus.status = 'failed';
                state.patchQuoteStatus.error = action.error.message;
            })
            // Convert Quote to Job
            .addCase(postConvertToJob.pending, (state) => {
                state.postConvertToJob.status = 'loading';
            })
            .addCase(postConvertToJob.fulfilled, (state) => {
                state.postConvertToJob.status = 'succeeded';
            })
            .addCase(postConvertToJob.rejected, (state, action) => {
                state.postConvertToJob.status = 'failed';
                state.postConvertToJob.error = action.error.message;
            })
            // Release Job
            .addCase(postReleaseJob.pending, (state) => {
                state.postReleaseJob.status = 'loading';
            })
            .addCase(postReleaseJob.fulfilled, (state) => {
                state.postReleaseJob.status = 'succeeded';
            })
            .addCase(postReleaseJob.rejected, (state, action) => {
                state.postReleaseJob.status = 'failed';
                state.postReleaseJob.error = action.error.message;
            })
            // Resubmit Job
            .addCase(postResubmitJob.pending, (state) => {
                state.postResubmitJob.status = 'loading';
            })
            .addCase(postResubmitJob.fulfilled, (state) => {
                state.postResubmitJob.status = 'succeeded';
            })
            .addCase(postResubmitJob.rejected, (state, action) => {
                state.postResubmitJob.status = 'failed';
                state.postResubmitJob.error = action.error.message;
            })
        ;
    }
});

export const selectGetQuotesArray = (state) => state.projects.getQuotes.array;
export const getQuotesStatus = (state) => state.projects.getQuotes.status;
export const getQuotesError = (state) => state.projects.getQuotes.error;

export const selectGetQuoteTemplatesById = (state) => state.projects.getQuoteTemplates.byId;
export const getQuoteTemplatesStatus = (state) => state.projects.getQuoteTemplates.status;

export const selectGetQuoteStatuses = (state) => state.projects.getQuoteStatuses.quoteStatuses;
export const getQuoteStatusesStatus = (state) => state.projects.getQuoteStatuses.status;

export const selectGetQuoteById = (state) => state.projects.getQuoteById.quotesById;

export const { quoteUpdatedFromStream } = projectsSlice.actions;
export default projectsSlice.reducer;